/**
 * Sale-label fallbacks + facet label/localization + breadcrumb helpers shared
 * by the SearchV2 facets and breadcrumbs. The range data itself
 * (start/end/label/bundleKey) lives in ./facetRanges.
 *
 * Pure data/format helpers only: controllers are built by the engine layer
 * (headless/engineDefinition), never here.
 */
import {
  ResourceBundles,
  microcopySetToResourceBundles,
} from '@lib/coveo/microcopySetToResourceBundles';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { DATE_RANGE_KEYS, PRICE_RANGES, RATING_RANGES } from './facetRanges';

const DAY_MS = 24 * 60 * 60 * 1000;

export const SALE_LABEL_FALLBACKS: Record<string, string> = {
  flash_deal: 'Flash deal',
  on_sale: 'On sale now',
  new_release_discount: 'New release discount',
  quick_start_bundle: 'Quick start bundle',
  on_sale_soon: 'On sale soon',
};

export const buildSaleBundle = (
  searchValuesMicrocopy: MicrocopyItem[] | undefined
): Record<string, string> | undefined =>
  microcopySetToResourceBundles(searchValuesMicrocopy ?? []).ec_sale_filters;

export const resolveSaleLabel = (
  bundle: Record<string, string> | undefined,
  value: string
): string => bundle?.[value] ?? SALE_LABEL_FALLBACKS[value] ?? value;

export const buildPriceBundle = (
  searchValuesMicrocopy: MicrocopyItem[] | undefined
): Record<string, string> | undefined =>
  microcopySetToResourceBundles(searchValuesMicrocopy ?? []).ec_price_filter;

export const resolvePriceLabel = (
  bundle: Record<string, string> | undefined,
  bundleKey: string,
  fallback: string
): string => bundle?.[bundleKey] ?? fallback;

/**
 * Look up a human-readable label for a numeric range breadcrumb.
 * Searches PRICE_RANGES and RATING_RANGES.
 */
export function getNumericRangeLabel(start: number, end: number): string {
  const allRanges = [...PRICE_RANGES, ...RATING_RANGES];
  const match = allRanges.find((r) => r.start === start && r.end === end);
  return match?.label ?? `${start} – ${end}`;
}

/**
 * Localized label for a numeric range breadcrumb: matches the range set for
 * the breadcrumb's field, resolves through its search-ui-values bundle (the
 * same bundle the facet list renders from), and falls back to the English
 * range label, then to a raw "start – end" string.
 */
export function resolveNumericRangeLabel(
  field: string,
  start: number,
  end: number,
  bundles: ResourceBundles
): string {
  const [ranges, bundle]: [typeof PRICE_RANGES, ResourceBundles[string]?] =
    field === 'ec_price_filter'
      ? [PRICE_RANGES, bundles.ec_price_filter]
      : field === 'ec_rating'
        ? [RATING_RANGES, bundles.ratings]
        : [[], undefined];
  const match = ranges.find((r) => r.start === start && r.end === end);
  if (!match) return getNumericRangeLabel(start, end);
  return bundle?.[match.bundleKey] ?? match.label;
}

const RELATIVE_PAST_DAYS = /^past-(\d+)-day/;

/** Coveo formats absolute date-range bounds as `YYYY/MM/DD@HH:mm:ss`. */
const parseCoveoDate = (value: string): number =>
  new Date(value.replace('@', 'T').replace(/\//g, '-')).getTime();

/**
 * Duration in days of a Coveo date-range value. The SSR engine defines the
 * release-date facet with *relative* ranges, which come back in controller
 * state (and breadcrumbs) as `past-N-day` / `now` strings; absolute
 * `start`/`end` timestamps are measured instead. NaN when neither form parses.
 */
export function getDateRangeDays(start: string, end: string): number {
  const relative = RELATIVE_PAST_DAYS.exec(start);
  if (relative) return Number(relative[1]);
  const startMs = parseCoveoDate(start);
  const endMs = end === 'now' ? Date.now() : parseCoveoDate(end);
  return Math.round((endMs - startMs) / DAY_MS);
}

/**
 * Look up a human-readable label for a date range breadcrumb.
 * Matches against DATE_RANGE_KEYS by the range's duration.
 */
export function getDateRangeLabel(start: string, end: string): string {
  const days = getDateRangeDays(start, end);
  const match = DATE_RANGE_KEYS.find((r) => r.days === days);
  return match?.label ?? `${start} – ${end}`;
}

/**
 * Localized label for a date range breadcrumb: matches DATE_RANGE_KEYS by the
 * range's duration, resolves through the first_published_at bundle (the same
 * bundle the ReleaseDateFacet list renders from), and falls back to the
 * English label, then to a raw "start – end" string.
 */
export function resolveDateRangeLabel(
  start: string,
  end: string,
  bundle: ResourceBundles[string] | undefined
): string {
  const days = getDateRangeDays(start, end);
  const match = DATE_RANGE_KEYS.find((r) => r.days === days);
  if (!match) return `${start} – ${end}`;
  return bundle?.[match.bundleKey] ?? match.label;
}
